import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { DataTable, EmptyState, PageHeader, Segmented, Stack, t } from '@kanbios/ui';
import type { fleetState } from '@kanbios/api-client';
import { Shell } from '../components/Shell';
import { RefusalPage } from '../components/Refusal';
import { useDisplayScope } from '../components/ScopePicker';
import { useEnvironmentFilter } from '../components/EnvironmentPicker';
import { ServerStateLegend, serverStateSpec } from '../components/ServerState';
import { healthColumn } from '../components/StateColumn';
import { api, unwrap } from '../lib/api';
import { shortDateTime } from '../lib/format';
import { useSession } from '../lib/session';
import { FULL_HEIGHT } from '../lib/tables';

type Issue = fleetState.components['schemas']['Issue'];
const VIEWS = ['all', 'incident', 'unknown', 'to_update', 'maintenance'] as const;
const PAGE_SIZE = 50;

/**
 * D72 « Alertes »: every cause of every server shown, its own health (D47) and
 * its applications' (D71), the gravest first, then the oldest; in the person's
 * scope and environment filter (D73). A row opens the server where it is said.
 */
export function AlertsPage() {
  const { enums } = useSession();
  const navigate = useNavigate();
  const scope = useDisplayScope();
  const env = useEnvironmentFilter();
  const [view, setView] = useState<(typeof VIEWS)[number]>('all');
  const [page, setPage] = useState(1);
  const list = useQuery({
    queryKey: ['issues', scope, env, view],
    queryFn: async () => unwrap(await api.fleetState.GET('/issues', { params: { query: { view, scope, env } } })),
    placeholderData: keepPreviousData,
    refetchInterval: 30_000,
  });
  const crumbs = [{ label: t('nav.alerts') }];
  if (list.error) return <Shell crumbs={crumbs}><RefusalPage error={list.error} /></Shell>;
  const items = list.data?.items ?? [];
  const shown = items.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const stateLabel = (state: string) => enums.states.find(s => s.value === state)?.label ?? state;
  return (
    <Shell crumbs={crumbs}>
      <Stack gap={24}>
        <PageHeader title={t('alerts.title')} subtitle={t('alerts.subtitle')} />
        <Segmented label={t('alerts.views')} size="sm" value={view} onChange={v => { setView(v as (typeof VIEWS)[number]); setPage(1); }}
          items={VIEWS.map(id => ({ id, label: id === 'all' ? t('alerts.all') : stateLabel(id), count: list.data?.views[id] }))} />
        {!list.isPending && !items.length ? <EmptyState icon="task_alt" title={t('alerts.empty')} /> : (
          <DataTable
            height={FULL_HEIGHT}
            label={t('alerts.table')}
            rows={shown}
            rowKey="id"
            loading={list.isPending}
            toolbar={false}
            density="compact"
            onRowClick={(i: Issue) => navigate(`/servers/${encodeURIComponent(i.server)}?tab=${i.kind === 'server' ? 'health' : 'applications'}`)}
            pagination={{ page, pageSize: PAGE_SIZE, total: items.length, onPageChange: setPage }}
            columns={[
              healthColumn<Issue>({
                legendLabel: t('servers.legend'), legendTitle: t('servers.legendTitle'), legend: <ServerStateLegend />,
                spec: i => serverStateSpec(enums, i.severity),
              }),
              { key: 'label', label: t('alerts.columns.label'), grow: true, minWidth: 280, sortable: false },
              { key: 'kind', label: t('alerts.columns.kind'), width: 130, sortable: false, render: (i: Issue) => t(`alerts.kind.${i.kind}`) },
              { key: 'server', label: t('alerts.columns.server'), width: 220, mono: true, sortable: false },
              { key: 'client', label: t('alerts.columns.client'), width: 180, sortable: false, render: (i: Issue) => i.client ?? '—' },
              { key: 'project', label: t('alerts.columns.project'), width: 180, sortable: false, render: (i: Issue) => i.project ?? '—' },
              { key: 'since', label: t('alerts.columns.since'), width: 150, sortable: false, render: (i: Issue) => (i.since ? shortDateTime(i.since) : '—') },
            ]}
          />
        )}
      </Stack>
    </Shell>
  );
}
